// src/components/ui/AnimatedNumber.tsx

import { useEffect, useRef, useState } from 'react';

interface AnimatedNumberProps {
  value: number;
  formatter: (value: number) => string;
  /** Duration of animation in seconds. Set to 0 to disable animation. */
  duration?: number;
}

export function AnimatedNumber({ 
  value, 
  formatter, 
  duration = 1.25
}: AnimatedNumberProps) {
  const [displayValue, setDisplayValue] = useState(value);
  const [formattedValue, setFormattedValue] = useState(formatter(value));
  const animationRef = useRef<number>();
  const prevValueRef = useRef<number>(value);
  
  // On mount and when value changes
  useEffect(() => {
    // Always update the formatted value immediately for the first render
    setFormattedValue(formatter(displayValue));
    
    // Skip if value hasn't changed or isn't valid
    if (value === prevValueRef.current || !isFinite(value) || isNaN(value)) {
      return;
    }
    
    // Skip animation if duration is 0
    if (duration === 0) {
      setDisplayValue(value);
      setFormattedValue(formatter(value));
      prevValueRef.current = value;
      return;
    }
    
    // Clean up any existing animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    
    const startTime = performance.now();
    const startValue = prevValueRef.current;
    const valueChange = value - startValue;
    const durationMs = duration * 1000;
    
    // Update the prev value ref
    prevValueRef.current = value;
    
    // Animation function
    const animateValue = (timestamp: number) => {
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / durationMs, 1);
      
      // Easing function - easeOutQuad
      const easing = 1 - (1 - progress) * (1 - progress);
      
      // Calculate current display value
      const currentValue = startValue + valueChange * easing;
      setDisplayValue(currentValue);
      setFormattedValue(formatter(currentValue));
      
      // Continue animation if not complete
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animateValue);
      }
    };
    
    // Start animation
    animationRef.current = requestAnimationFrame(animateValue);
    
    // Cleanup function
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [value, formatter, duration, displayValue]);
  
  return <span>{formattedValue}</span>;
}